import NiceModal from "@ebay/nice-modal-react";
import { Box, Divider, Table, TableContainer, TableRow, Typography } from "@mui/material";
import { NdpInsuredPerson, ProductClasType, ProductNameType } from "@repo/api";
import { isNil } from "lodash";
import { FC, useCallback } from "react";
import { BodyTextM, Icons, Modals } from "..";
import { formatDate } from "../../helpers";
import { useTypedTranslation } from "../../hooks";
import {
    formatFufiCoverageInsuredAmount,
    formatFufiCoveragePremium,
    useFufiCoverageNameFormatter,
    useFufiCoverageStartDateFormatter,
} from "./InsuranceCoverage.helpers";
import * as Styled from "./InsuranceCoverageAccordion.styled";

const TextModalContent: FC<{ text: string }> = ({ text }) => <Typography>{text}</Typography>;

type InsuranceCoverageDesktopBodyContentProps = {
    person: NdpInsuredPerson;
    productClass?: ProductClasType;
    insuranceTechnicalStartDate?: Date;
    isOwner?: boolean;
    productName?: ProductNameType | null;
};

export const InsuranceCoverageDesktopBodyContent: FC<InsuranceCoverageDesktopBodyContentProps> = ({
    person,
    productClass,
    insuranceTechnicalStartDate,
    isOwner,
    productName,
}) => {
    const { t } = useTypedTranslation();
    const coverageNameFormatter = useFufiCoverageNameFormatter();
    const coverageStartDateFormatter = useFufiCoverageStartDateFormatter();
    const isUlInitialUnits = productName === ProductNameType.NnFufiUl;

    const handleShowTooltip = useCallback(
        (title: string, text: string) => {
            NiceModal.show(Modals.ModalConfirm, {
                title: title,
                content: TextModalContent,
                additionalContentProps: { text },
                confirmButtonText: t("global.iUnderstand"),
            });
        },
        [t],
    );

    return (
        <>
            <Styled.InsuranceCoverageWrapperBox>
                <TableContainer>
                    <Table>
                        <Styled.InsuranceCoverageTableBody>
                            <TableRow>
                                <Styled.BoldCell $width={270}> </Styled.BoldCell>
                                <Styled.BoldCell $align="left">
                                    {t("life.contractDetail.coverags.table.tarif")}
                                </Styled.BoldCell>
                                <Styled.BoldCell $align="right" $width={110}>
                                    {t("life.contractDetail.coverags.table.stardDate")}
                                </Styled.BoldCell>
                                <Styled.BoldCell $align="right" $width={110}>
                                    {t("life.contractDetail.coverags.table.endDate")}
                                </Styled.BoldCell>
                                <Styled.BoldCellNoWrap $align="right" $width={105}>
                                    {t("life.contractDetail.coverags.table.premium")}
                                    {productClass === ProductClasType.Ul && (
                                        <Styled.InfoListItemButton
                                            $noRightPadding
                                            disableRipple
                                            onClick={() =>
                                                handleShowTooltip(
                                                    t("life.contractDetail.coverags.table.premium"),
                                                    t("life.contractDetail.coverags.table.premiumTooltip"),
                                                )
                                            }
                                        >
                                            <Icons.Info />
                                        </Styled.InfoListItemButton>
                                    )}
                                </Styled.BoldCellNoWrap>
                                <Styled.BoldCell $align="right" $width={135}>
                                    {t("life.contractDetail.coverags.table.insuredAmount")}
                                </Styled.BoldCell>
                                <Styled.BoldCell $align="right" $width={80}>
                                    {t("life.contractDetail.coverags.table.exclusion")}
                                </Styled.BoldCell>
                            </TableRow>
                            {person.coverages
                                .slice()
                                .sort((a, b) => a.order - b.order)
                                .map((coverage) => {
                                    const insuredAmountValue = formatFufiCoverageInsuredAmount(
                                        coverage,
                                        productClass,
                                        t("global.emptyValue"),
                                        productName,
                                    );
                                    const premiumDisplay = formatFufiCoveragePremium(coverage, t("global.emptyValue"), productName);

                                    return (
                                        <TableRow key={coverage.id}>
                                            <Styled.BoldCell>
                                                {coverageNameFormatter(coverage, isOwner, productClass, productName)}
                                            </Styled.BoldCell>
                                            <Styled.NoWrapCell>{coverage.code ?? t("global.emptyValue")}</Styled.NoWrapCell>
                                            <Styled.NoWrapCell $align="right">
                                                {coverageStartDateFormatter(
                                                    coverage.dateStart,
                                                    insuranceTechnicalStartDate,
                                                )}
                                            </Styled.NoWrapCell>
                                            <Styled.NoWrapCell $align="right">
                                                {formatDate(coverage.dateEnd, t("global.emptyValue"))}
                                            </Styled.NoWrapCell>
                                            <Styled.NoWrapCell $align="right">
                                                {premiumDisplay}
                                            </Styled.NoWrapCell>
                                            <Styled.NoWrapCell $align="right">{insuredAmountValue}</Styled.NoWrapCell>
                                            <Styled.NoWrapCell $align="right">
                                                {!isNil(coverage.exclusion) && (
                                                    <Styled.InfoListItemButton
                                                        disableRipple
                                                        onClick={() =>
                                                            handleShowTooltip(
                                                                t("life.contractDetail.coverags.table.exclusion"),
                                                                coverage.exclusion ?? "",
                                                            )
                                                        }
                                                    >
                                                        <Icons.Info />
                                                    </Styled.InfoListItemButton>
                                                )}
                                                {!isNil(coverage.exclusion) ? t("global.yes") : t("global.no")}
                                            </Styled.NoWrapCell>
                                        </TableRow>
                                    );
                                })}
                        </Styled.InsuranceCoverageTableBody>
                    </Table>
                </TableContainer>
            </Styled.InsuranceCoverageWrapperBox>
            <Divider />
            <Styled.InsuranceCoverageWrapperBox>
                {isUlInitialUnits ? (
                    <TableContainer>
                        <Table>
                            <Styled.InsuranceCoverageTableBody>
                                <TableRow>
                                    <Styled.BoldCell $width={270}>
                                        {t("life.contractDetail.coverags.beneficiaries")}
                                        <Styled.InfoListItemButton
                                            disableRipple
                                            onClick={() =>
                                                handleShowTooltip(
                                                    t("life.contractDetail.coverags.beneficiaries"),
                                                    t("life.contractDetail.coverags.beneficiariesTooltip"),
                                                )
                                            }
                                        >
                                            <Icons.Info />
                                        </Styled.InfoListItemButton>
                                    </Styled.BoldCell>
                                    <Styled.NoWrapCell>
                                        {person.fufiBeneficiaryString ?? t("life.contractDetail.coverags.noBeneficiaries")}
                                    </Styled.NoWrapCell>
                                </TableRow>
                            </Styled.InsuranceCoverageTableBody>
                        </Table>
                    </TableContainer>
                ) : (
                    <>
                        <Styled.ItemsBetweenStack>
                            <Box>
                                <Styled.BoldBodyTextM>
                                    {t("life.contractDetail.coverags.beneficiaries")}
                                </Styled.BoldBodyTextM>
                                <Styled.InfoListItemButton
                                    disableRipple
                                    onClick={() =>
                                        handleShowTooltip(
                                            t("life.contractDetail.coverags.beneficiaries"),
                                            t("life.contractDetail.coverags.beneficiariesTooltip"),
                                        )
                                    }
                                >
                                    <Icons.Info />
                                </Styled.InfoListItemButton>
                            </Box>
                        </Styled.ItemsBetweenStack>
                        <BodyTextM>
                            {person.fufiBeneficiaryString ?? t("life.contractDetail.coverags.noBeneficiaries")}
                        </BodyTextM>
                    </>
                )}
            </Styled.InsuranceCoverageWrapperBox>
        </>
    );
};
